import type { Guide } from '@fruitful/types';
import { useVideoPlayer, VideoView } from 'expo-video';
import React, { useEffect, useState } from 'react';
import { Pressable } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    BodySmallRegular,
    ButtonVariant,
    CloseButton,
    FootnoteRegular,
    H5Emphasized,
    IconButton,
    Image,
    isNonMobileWeb,
    isWeb,
    PlayIcon,
    ScrollView,
    useMedia,
    View,
    Volume2Icon,
    VolumeOffIcon,
    XStack,
    YStack,
} from 'smoothie';

import { getGuideBioVideoUrls } from '../GuideMedia';

interface GuideDetailsContentProps {
    guide: Guide;
    containerHeight: number;
    containerWidth: number;
    onOpenChange?: (isOpen: boolean) => void;
    isSheetLayout?: boolean;
}

interface GuideProfileVideoPlaybackProps {
    mp4Uri: string;
    posterUri: string;
    videoSize: number;
    isMuted: boolean;
    isPaused: boolean;
    onTogglePause: () => void;
}

const GuideProfileVideoPlayback = ({
    mp4Uri,
    posterUri,
    videoSize,
    isMuted,
    isPaused,
    onTogglePause,
}: GuideProfileVideoPlaybackProps) => {
    const player = useVideoPlayer(mp4Uri, (p) => {
        p.loop = true;
    });
    const [showPoster, setShowPoster] = useState(true);

    useEffect(() => {
        setShowPoster(true);
    }, [mp4Uri]);

    // On web, expo-video's onFirstFrameRender can miss loadstart/loadeddata (race after
    // mount), so also hide the poster from player readiness / playback signals. Native
    // can rely on onFirstFrameRender alone.
    useEffect(() => {
        if (!isWeb) {
            return;
        }

        if (player.status === 'readyToPlay' || player.currentTime > 0) {
            setShowPoster(false);
            return;
        }

        const hidePoster = () => {
            setShowPoster(false);
            statusSubscription.remove();
            playingSubscription.remove();
        };

        const statusSubscription = player.addListener(
            'statusChange',
            ({ status }) => {
                if (status === 'readyToPlay') {
                    hidePoster();
                }
            },
        );
        const playingSubscription = player.addListener(
            'playingChange',
            ({ isPlaying }) => {
                if (isPlaying) {
                    hidePoster();
                }
            },
        );

        return () => {
            statusSubscription.remove();
            playingSubscription.remove();
        };
    }, [player, mp4Uri]);

    useEffect(() => {
        const updatePlaybackState = async () => {
            player.muted = isMuted;

            if (isPaused) {
                player.pause();
            } else {
                try {
                    // On web, player.play() returns a Promise that can reject if autoplay is blocked
                    // TypeScript types it as void, but it actually returns a Promise on web platforms
                    // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression -- player.play() returns Promise on web
                    await player.play();
                } catch (error) {
                    // If autoplay is blocked by the browser, fall back to paused state
                    if (
                        error instanceof Error &&
                        error.name === 'NotAllowedError'
                    ) {
                        onTogglePause();
                    }
                }
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- intentionally not awaiting, cleanup not needed
        updatePlaybackState();
    }, [player, isMuted, isPaused, onTogglePause]);

    return (
        <Pressable onPress={onTogglePause}>
            <View
                height={videoSize}
                width={videoSize}
                $platform-web={{ position: 'relative' }}
            >
                <VideoView
                    style={{ width: '100%', height: '100%' }}
                    // @ts-expect-error expo-video typings intersect `VideoPlayerWeb` into `VideoView`; `useVideoPlayer` is correct at runtime.
                    player={player}
                    nativeControls={false}
                    contentFit="contain"
                    playsInline={isWeb}
                    {...(isWeb && {
                        // Cross-origin `<video>` needs CORS for reliable audio on web; CloudFront for GuideMedia URLs must emit
                        // `Access-Control-Allow-Origin` (and allow Range) or playback fails with NotSupportedError.
                        crossOrigin: 'anonymous' as const,
                    })}
                    onFirstFrameRender={() => {
                        setShowPoster(false);
                    }}
                />
                {showPoster && (
                    <View
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        pointerEvents="none"
                    >
                        <Image
                            source={{ uri: posterUri }}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </View>
                )}
                {isPaused && (
                    <View
                        position="absolute"
                        height="100%"
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <PlayButton
                            onPress={() => {
                                // Do nothing because ancestor Pressable onPress handles this.
                                // Passing onTogglePause here as well can cause infinite loop error (at least on web).
                            }}
                        />
                    </View>
                )}
            </View>
        </Pressable>
    );
};

export const GuideDetailsContent = ({
    guide,
    onOpenChange,
    containerHeight,
    containerWidth,
    isSheetLayout = false,
}: GuideDetailsContentProps) => {
    const insets = useSafeAreaInsets();
    const breakpoints = useMedia();

    const [isMuted, setIsMuted] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const videoUrls = getGuideBioVideoUrls(guide.signUpId);
    const shouldUseFallbackImage = videoUrls === null;

    const mobileVideoSize = containerWidth;
    const nonMobileVideoSize = 400;

    const toggleVideoPause = () => {
        setIsPaused((prev) => !prev);
    };

    return (
        <View
            display="flex"
            flexDirection="column"
            $sm={{
                flexDirection: 'row',
                alignItems: 'center',
            }}
        >
            <YStack
                height={mobileVideoSize}
                $sm={{
                    height: nonMobileVideoSize,
                    width: nonMobileVideoSize,
                    borderRadius: 30,
                    overflow: 'hidden',
                }}
            >
                {shouldUseFallbackImage ? (
                    <GuideImageFallback guide={guide} />
                ) : (
                    <GuideProfileVideoPlayback
                        mp4Uri={videoUrls.mp4Url}
                        posterUri={videoUrls.posterUrl}
                        videoSize={
                            breakpoints.sm
                                ? nonMobileVideoSize
                                : mobileVideoSize
                        }
                        isMuted={isMuted}
                        isPaused={isPaused}
                        onTogglePause={toggleVideoPause}
                    />
                )}

                <XStack
                    position="absolute"
                    marginTop={isSheetLayout ? 24 : insets.top + 4}
                    paddingHorizontal={isSheetLayout ? 24 : 12}
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection={isSheetLayout ? 'row-reverse' : 'row'}
                >
                    <XStack
                        height={40}
                        width={40}
                        alignItems="center"
                        justifyContent="flex-start"
                    >
                        {isSheetLayout ? (
                            <CloseButton
                                onPress={() => {
                                    onOpenChange?.(false);
                                }}
                                background="gray"
                            />
                        ) : (
                            <BackButton
                                $sm={{ display: 'none' }}
                                background="transparent"
                                iconType={BackButtonIconType.ARROW_LEFT}
                                customBackFn={() => {
                                    onOpenChange?.(false);
                                }}
                            />
                        )}
                    </XStack>
                    {!shouldUseFallbackImage && (
                        <XStack
                            height={40}
                            width={40}
                            alignItems="center"
                            justifyContent="flex-end"
                        >
                            <MuteButton
                                variant={
                                    isSheetLayout ? 'secondary' : 'tertiary'
                                }
                                isMuted={isMuted}
                                onPress={() => {
                                    setIsMuted((prev) => !prev);
                                }}
                            />
                        </XStack>
                    )}
                </XStack>
            </YStack>

            <ScrollView
                showsVerticalScrollIndicator={isNonMobileWeb}
                scrollbarWidth="thin"
                maxHeight={
                    isNonMobileWeb
                        ? nonMobileVideoSize
                        : containerHeight - mobileVideoSize
                }
                paddingBottom={isSheetLayout ? 24 : 0}
            >
                <YStack
                    gap={16}
                    paddingHorizontal={20}
                    marginVertical={20}
                    maxWidth={450}
                    paddingBottom={isNonMobileWeb ? 0 : 24}
                    $md={{ paddingLeft: 32 }}
                >
                    <XStack alignItems="center" gap={12}>
                        <H5Emphasized>{guide.firstName}</H5Emphasized>
                        <FootnoteRegular color="$gray1100" marginTop={5}>
                            CFP&reg;
                        </FootnoteRegular>
                    </XStack>
                    <XStack gap={12} flexWrap="wrap" $sm={{ marginBottom: 0 }}>
                        {guide.styles?.split(',').map((style: string) => (
                            <View
                                key={style}
                                backgroundColor="$gray100"
                                borderRadius={60}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                padding={8}
                            >
                                <BodySmallRegular color="$gray1200">
                                    {style}
                                </BodySmallRegular>
                            </View>
                        ))}
                    </XStack>
                    <BodyMediumRegular color="$gray1100">
                        {guide.bio}
                    </BodyMediumRegular>
                </YStack>
            </ScrollView>
            <View
                position="absolute"
                top={0}
                right={0}
                display="none"
                $sm={{ display: 'flex' }}
            >
                <CloseButton
                    background="gray"
                    onPress={() => {
                        onOpenChange?.(false);
                    }}
                />
            </View>
        </View>
    );
};

interface MuteButtonProps {
    variant: 'tertiary' | 'secondary';
    isMuted: boolean;
    onPress: () => void;
}

const MuteButton = ({ variant, isMuted, onPress }: MuteButtonProps) => {
    return (
        <IconButton
            variant={variant}
            icon={isMuted ? VolumeOffIcon : Volume2Icon}
            onPress={onPress}
        />
    );
};

const PlayButton = ({ onPress }: { onPress?: () => void }) => {
    return (
        <IconButton
            variant={ButtonVariant.SECONDARY}
            icon={PlayIcon}
            onPress={() => {
                onPress?.();
            }}
        />
    );
};

interface GuideImageFallbackProps {
    guide: Guide;
}
const GuideImageFallback = ({ guide }: GuideImageFallbackProps) => {
    return (
        <Image
            source={{ uri: guide.profileImageUrl ?? '' }}
            style={{ width: '100%', height: '100%' }}
        />
    );
};
